import React, { ReactElement } from "react";
import Mousetrap from "@timeedit/mousetrap";
import _ from "underscore";

/**
 * Where the popover points. Every caller measures an element and passes pixels,
 * or "center" to place the popover in the middle of the window with no arrow.
 * The one caller that anchors to the bottom passes bottom instead of top.
 */
type TPopoverTarget =
    | "center"
    | {
          top?: number;
          left?: number;
          bottom?: number;
          width?: number;
          height?: number;
      };

/**
 * The style the popover works out for itself. React.CSSProperties alone types
 * top and left as csstype's branded properties, which cannot be added up; these
 * are pixels. width is the exception: it comes from the caller's own style and
 * two callers give it as "200px" or "50%".
 */
type TPopoverStyle = React.CSSProperties & {
    top?: number;
    left?: number;
    bottom?: number;
    height?: number;
    maxHeight?: number;
};

type TPopoverProps = {
    onClose: () => void;
    target: TPopoverTarget;
    style: React.CSSProperties;
    bottomArrow?: boolean;
    children?: ReactElement;
};

type TPopoverState = {
    style: TPopoverStyle;
};

class Popover extends React.Component<TPopoverProps, TPopoverState> {
    static displayName = "Popover";

    static defaultProps = {
        style: {},
    };
    static DEFAULT_WIDTH: number;
    _isMounted?: boolean;
    _previousMouseBindings: any;
    _content = React.createRef<HTMLDivElement>();

    componentDidMount() {
        this._isMounted = true;
        const self = this;

        this._previousMouseBindings = Mousetrap.unbindWithHelp("esc", true)[0];
        Mousetrap.bindWithHelp("esc", () => {
            if (self && self.props) {
                self.props.onClose();
            }
            return false;
        });
        this.updateStyle();
    }

    componentDidUpdate(prevProps) {
        this.updateStyle(this.props, prevProps);
    }

    componentWillUnmount() {
        this._isMounted = false;
        Mousetrap.bindWithHelp("esc", this._previousMouseBindings);
    }

    getStyle = (props: TPopoverProps): TPopoverStyle => {
        const MIN_DISTANCE_TOP = 22;
        const target = props.target;
        const content = this._content.current;

        if (target === "center") {
            if (!this._isMounted || !content) {
                return { top: 0, left: 0 };
            }

            return _.extend({}, props.style, {
                top: MIN_DISTANCE_TOP,
                /*top: Math.max(
                    // eslint-disable-next-line no-magic-numbers
                    Math.floor((document.body.clientHeight - content.offsetHeight) / 2),
                    MIN_DISTANCE_TOP
                ),*/
                // eslint-disable-next-line no-magic-numbers
                left: Math.floor((document.body.clientWidth - content.offsetWidth) / 2),
                maxHeight: document.body.clientHeight,
            });
        }

        const style: TPopoverStyle = _.extend({}, props.style, {
            left: (target.left ?? 0) + (target.width ?? 0),
            top: target.top,
        });

        // Count from bottom if value is provided
        if (target.bottom) {
            style.top = undefined;
            style.bottom = target.bottom;
        }

        if (!this._isMounted || !content) {
            return style;
        }

        // Only a width in pixels can be added to a position; two callers give
        // theirs as "200px" or "50%", and those keep the left edge they had.
        const width = typeof style.width === "number" ? style.width : null;
        if (width !== null && (style.left ?? 0) + width > document.body.clientWidth) {
            const targetLeft = target.left ?? 0;
            if (targetLeft - width > 0) {
                style.left = targetLeft - width;
            } else {
                style.left = targetLeft;
            }
        }

        // top is undefined while the popover is anchored to the bottom, where
        // each of these comparisons was NaN, and so never true.
        if (
            style.top !== undefined &&
            style.top + content.offsetHeight > document.body.clientHeight
        ) {
            style.top = (target.top ?? 0) + (target.height ?? 0) - content.offsetHeight;
        }

        if (style.top !== undefined && style.top < 0) {
            style.top = 0;
        }

        const boundingRect = content.getBoundingClientRect();
        style.height = boundingRect.height;
        style.maxHeight = document.body.clientHeight;
        if (style.bottom) {
            style.maxHeight = boundingRect.bottom;
        }
        if (!target.bottom && style.top !== undefined && style.top < MIN_DISTANCE_TOP) {
            style.top = MIN_DISTANCE_TOP;

            if ((style.maxHeight ?? 0) + style.top > document.body.clientHeight) {
                style.maxHeight = document.body.clientHeight - style.top;
            }
        }
        if (style.top === 0) {
            style.top = undefined;
        }
        return style;
    };

    updateStyle = (props = this.props, prevProps?: TPopoverProps) => {
        if (prevProps) {
            if (prevProps.target === this.props.target && _.isEqual(prevProps.style, props.style)) {
                return;
            }
        }
        const style = this.getStyle(props);
        if (!_.isEqual(style, this.state.style)) {
            this.setState({ style });
        }
    };

    state: TPopoverState = {
        style: this.getStyle(this.props),
    };

    render() {
        const classes = ["popup"];
        const style = _.clone(this.state.style);
        const target = this.props.target === "center" ? null : this.props.target;
        const left = style.left ?? 0;
        // The arrow is placed in pixels, so a width the caller gave as "200px"
        // or "50%" cannot take part: the arrow sits at the popover's own edge.
        const width = typeof style.width === "number" ? style.width : 0;
        const arrowStyle: {
            top?: number;
            left?: number;
        } = {
            top: target?.top,
        };

        if (this.props.bottomArrow) {
            classes.push("below");
            // eslint-disable-next-line no-magic-numbers
            arrowStyle.left = (left + width) / 2;
        } else {
            if (target?.left !== undefined && left > target.left) {
                classes.push("left");
                arrowStyle.left = left;
            } else {
                classes.push("right");
                arrowStyle.left = left + width;
            }

            if (style.top !== undefined && target?.top !== undefined && style.top < target.top) {
                classes.push("bottom");
            }
        }
        delete style.height;
        // A popover holds one child, which it lets ask for a re-measure.
        const children = this.props.children
            ? React.cloneElement(this.props.children, { onContentChange: this.updateStyle })
            : null;
        return (
            <div className={classes.join(" ")}>
                <div ref={this._content} style={style}>
                    {children}
                </div>
                {this.props.target === "center" ? null : (
                    <span className="arrow" style={arrowStyle} />
                )}
            </div>
        );
    }
}

Popover.DEFAULT_WIDTH = 350;

export default Popover;
